import { Injectable, inject, signal, NgZone } from '@angular/core';
import { getFirestore, doc, getDoc, setDoc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AuthService } from './auth.service';
import { AppUser } from '../models/user.model';

export interface Organization {
  id: string;
  name: string;
  ownerId?: string;
  creditBalance?: number;
  /** Managing partner org id (client orgs) — drives catalog overrides and white-label branding. */
  partnerId?: string;
  /** Reseller flag — a partner org sees its own brand/catalog overrides. */
  isPartner?: boolean;
}

@Injectable({ providedIn: 'root' })
export class OrgContextService {
  private readonly auth = inject(AuthService);
  private readonly zone = inject(NgZone);

  readonly userOrganizations = signal<Organization[]>([]);
  readonly currentOrg = signal<Organization | null>(null);

  // Cross-app shared selection (users/{uid}.currentOrganizationId), the same
  // field admin/connect/open-webui use. Captured in loadUserOrganizations.
  private remoteCurrentOrgId: string | null = null;
  private orgUnsub: Unsubscribe | null = null;

  private get storageKey(): string {
    const uid = this.auth.currentUser()?.uid;
    return `build-current-org-${uid}`;
  }

  async init(): Promise<void> {
    const user = this.auth.currentUser();
    if (!user) return;

    await this.loadUserOrganizations();

    // Prefer the cross-app shared selection (user doc), then this app's
    // localStorage, then the first org.
    const orgs = this.userOrganizations();
    const savedOrgId = localStorage.getItem(this.storageKey);
    const targetId = [this.remoteCurrentOrgId, savedOrgId].find(
      (id): id is string => !!id && orgs.some((o) => o.id === id),
    );

    if (targetId) {
      this.currentOrg.set(orgs.find((o) => o.id === targetId)!);
    } else if (orgs.length > 0) {
      this.currentOrg.set(orgs[0]);
    }
    this.startOrgBalanceListener(this.currentOrg()?.id ?? null);
  }

  async loadUserOrganizations(): Promise<void> {
    const user = this.auth.currentUser();
    if (!user) return;

    const db = getFirestore();
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.data();
    const orgIds: string[] = userData?.['organizationIds'] ?? [];
    this.remoteCurrentOrgId = (userData?.['currentOrganizationId'] as string) ?? null;

    const orgs: Organization[] = [];
    for (const orgId of orgIds) {
      try {
        const orgDoc = await getDoc(doc(db, 'organizations', orgId));
        if (orgDoc.exists()) {
          const data = orgDoc.data();
          orgs.push({
            id: orgId,
            name: data['name'] || orgId,
            ownerId: data['ownerId'],
            creditBalance: data['creditBalance'],
            partnerId: data['partnerId'],
            isPartner: data['isPartner'],
          });
        }
      } catch {
        // Skip orgs user can no longer access
      }
    }

    this.zone.run(() => {
      this.userOrganizations.set(orgs);
    });
  }

  switchOrg(orgId: string): void {
    const org = this.userOrganizations().find((o) => o.id === orgId);
    if (org) {
      this.currentOrg.set(org);
      this.startOrgBalanceListener(orgId);
      localStorage.setItem(this.storageKey, orgId);
      // Share the selection across apps via the user doc (only on change).
      const uid = this.auth.currentUser()?.uid;
      if (uid && this.remoteCurrentOrgId !== orgId) {
        setDoc(
          doc(getFirestore(), 'users', uid),
          { currentOrganizationId: orgId },
          { merge: true },
        ).catch(() => undefined);
      }
      this.remoteCurrentOrgId = orgId;
    }
  }

  /**
   * Fetch members of the given org (defaults to currentOrg).
   *
   * Uses the `listMembersDetailsOfOrganization` Cloud Function which reads from
   * Firebase Auth — the canonical source for display names regardless of how
   * the user signed up (Google OAuth, email/password, etc.).
   * Profile pictures are supplemented from Firestore `users/{uid}` docs.
   */
  async getOrgMembers(orgId?: string, options: { memberIds?: string[]; search?: string; limit?: number } = {}): Promise<AppUser[]> {
    const resolvedOrgId = orgId ?? this.currentOrg()?.id;
    if (!resolvedOrgId) return [];

    try {
      const listMembers = httpsCallable<
        { organizationId: string; memberIds?: string[]; search?: string; limit?: number },
        { users: Record<string, { displayName: string; email: string }> }
      >(getFunctions(undefined, 'northamerica-northeast1'), 'listMembersDetailsOfOrganization');

      const result = await listMembers({
        organizationId: resolvedOrgId,
        ...(options.memberIds ? { memberIds: options.memberIds } : {}),
        ...(options.search ? { search: options.search } : {}),
        ...(options.limit ? { limit: options.limit } : {}),
      });
      const memberDetails = result.data.users;

      const uids = Object.keys(memberDetails);
      if (uids.length === 0) return [];

      return await this.hydrateAppUsers(memberDetails, uids);
    } catch (err) {
      console.warn('[org-context] Could not fetch org members:', err);
      return [];
    }
  }

  async getOrgMembersByIds(orgId: string | undefined, memberIds: string[]): Promise<AppUser[]> {
    const uniqueMemberIds = Array.from(new Set(memberIds.filter(Boolean)));
    if (uniqueMemberIds.length === 0) return [];
    return this.getOrgMembers(orgId, { memberIds: uniqueMemberIds });
  }

  async searchOrgMembers(orgId: string | undefined, search: string, limit = 20): Promise<AppUser[]> {
    const queryText = search.trim();
    if (queryText.length < 2) return [];
    return this.getOrgMembers(orgId, { search: queryText, limit });
  }

  private async hydrateAppUsers(
    memberDetails: Record<string, { displayName: string; email: string }>,
    uids: string[]
  ): Promise<AppUser[]> {
    // Supplement with profile pictures from Firestore (Auth doesn't expose them)
    const db = getFirestore();
    const userFetches = uids.map(async (uid): Promise<AppUser> => {
      const details = memberDetails[uid];
      const user: AppUser = {
        uid,
        displayName: details.displayName || '',
        email: details.email || '',
        createdAt: new Date(),
      };
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (snap.exists()) {
          const data = snap.data();
          if (data['profilePictureUrl']) user.profilePictureUrl = data['profilePictureUrl'] as string;
          if (data['profilePictureStoragePath']) user.profilePictureStoragePath = data['profilePictureStoragePath'] as string;
          if (data['createdAt']) user.createdAt = (data['createdAt'] as import('firebase/firestore').Timestamp)?.toDate?.() ?? new Date();
        }
      } catch {
        // Profile picture is optional — proceed without it
      }
      return user;
    });

    return Promise.all(userFetches);
  }

  private startOrgBalanceListener(orgId: string | null): void {
    this.orgUnsub?.();
    this.orgUnsub = null;
    if (!orgId) return;
    const db = getFirestore();
    this.orgUnsub = onSnapshot(
      doc(db, 'organizations', orgId),
      (snap) => {
        if (!snap.exists()) return;
        const balance = snap.data()['creditBalance'];
        const current = this.currentOrg();
        if (current && current.id === orgId) {
          this.zone.run(() => {
            this.currentOrg.set({
              ...current,
              creditBalance: typeof balance === 'number' ? balance : current.creditBalance,
            });
          });
        }
      },
      () => { /* ignore errors — balance becomes stale but app still works */ }
    );
  }
}
